export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  /** Reference value in USD cents, shown struck through next to "Included free" */
  referenceValueCents?: number
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  secondaryNote?: string
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Welding Machine Settings & Parameters Quick-Reference Charts',
    subtitle: 'The Right Starting Point at Every Booth',
    referenceValueCents: 14990,
    intro:
      'Printable quick-reference charts for welding instructors and students. Each chart is clean, labeled and ready to post at the welding booth or hand out in class. Always tune settings on scrap and follow the manufacturer data and the WPS.',
    bulletsIntro: 'Includes:',
    bullets: [
      'MIG/MAG starting settings by plate thickness',
      'Wire diameter, wire feed speed, voltage & gas flow',
      'TIG tungsten type, size & amperage ranges',
      'TIG polarity by metal, argon flow & post-flow',
      'Filler metal selection',
      'Stick electrode classification decoder',
      'E6010, E6011, E6013 & E7018 amperage ranges',
      'Lens shade selection',
      'Fraction-to-millimeter conversions & duty cycle examples',
    ],
    result: 'Help students set up any machine with confidence and spend more time welding, less time guessing.',
  },
  {
    id: '02',
    title: 'Welding Demonstration Lesson Scripts',
    subtitle: 'Run a Polished Demonstration Without Preparing From Scratch',
    referenceValueCents: 9990,
    intro:
      'Ready-to-read demonstration scripts that follow the 4-step method (show, explain, student explains, student does), each with timing, materials and PPE list, safety reminders, key instructor phrases, common student mistakes and a quick check at the end.',
    bulletsIntro: 'Includes:',
    bullets: [
      'PPE & booth safety briefing',
      'Setting up a MIG machine',
      'Striking an arc & running stringer beads with stick',
      'Horizontal fillet welds with MIG',
      'TIG puddle control & adding filler',
      'Vertical-up technique',
      'Fit-up & tack welding',
      'Visual inspection with a fillet gauge',
    ],
    result: 'Teach every demonstration with the confidence of a script that already works.',
  },
  {
    id: '03',
    title: 'Welding Practice Projects & Coupon Exercise Workbook',
    subtitle: 'Structured Booth Practice, Week After Week',
    referenceValueCents: 7990,
    intro:
      'A practice workbook of welding exercises. Each exercise has the coupon size and preparation, process and consumable, target settings range, step-by-step instructions, an acceptance checklist for visual inspection and space for instructor feedback.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Stringer-bead pads',
      'Lap, corner & tee joints in 1F to 4F',
      'Groove welds with backing in 1G to 3G',
      'Open-root practice',
      'Sheet-metal butt joints',
      'TIG outside corners',
      'Small fabrication projects from simple drawings',
      'Acceptance checklists & feedback space',
    ],
    result: 'Give students the repetition they need to build consistent, inspection-ready welds.',
  },
  {
    id: '04',
    title: 'Weld Defect Identification Flashcards',
    subtitle: 'Train Students to See, Name and Prevent Every Defect',
    referenceValueCents: 6990,
    intro:
      'Printable visual flashcards with a clean labeled cross-section and surface view on the front, and on the back the most likely causes, how to prevent it in MIG, TIG and stick welding, an instructor cue and a quick-check question.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Porosity',
      'Undercut & overlap (cold lap)',
      'Incomplete fusion & incomplete penetration',
      'Slag inclusions',
      'Cracks',
      'Excessive reinforcement & underfill',
      'Spatter & arc strikes',
      'Burn-through & distortion',
    ],
    result: 'Use them for warm-ups, bead-diagnosis games, inspection practice and quizzes.',
  },
  {
    id: '05',
    title: 'Welding Competency Checklists, Rubrics & Certificates',
    subtitle: 'Evaluate Objectively and Document Every Skill Achieved',
    referenceValueCents: 5990,
    intro:
      'Ready-to-print assessment tools for welding instructors and technical schools: beginner, intermediate and advanced competency checklists, scoring rubrics for practice coupons, fillet and groove welds, a student skills-progress tracker and editable course-completion certificates for each level.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Safety & setup',
      'Stick, MIG & flux-cored skills',
      'TIG skills',
      'Positions & fit-up',
      'Print reading & visual inspection',
      'Coupon, fillet & groove rubrics',
      'Skills-progress tracker',
      'Course-completion certificates',
    ],
    result: 'Assess every student fairly and give structured feedback at every level.',
  },
]
